import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Redirect, useHistory } from "react-router-dom";
import { FormikHelpers, FormikValues } from "formik";

import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import {
  AssignAnyProjectAndUserToJob,
  EditAssignedJob,
  EditJob as EditJobPermission,
} from "../../../../services/authorization/Permissions";
import {
  EditJobType,
  EditJobName,
  EditProject,
  EditJobStatus,
  EditPriority,
} from "../../components/DataComponents";
import {
  DataSection,
  SubSection,
  EditDescriptionSection,
} from "../../../../components/data-components/sections/DataSections";
import { dateService } from "../../../../services/date-service/DateService";
import { CurrentJobStorageModel } from "../../../../services/job/CurrentJobStorage";
import { JobInput } from "../../../../api-models/request/JobInput";
import EditAssignedUser from "../../../../components/data-components/EditAssignedUser";
import PageLayout from "../../../_layout/PageLayout";
import Form from "../../../../components/form/Form";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { EditAvailableFromAndDeadlineDates } from "../../../projects/components/DataComponents";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { api } from "../../../../services/api/ApiService";
import { JobDynamicMenuItemsPages, JobMenuItemsModel } from "../../JobMenuItems";
import { warningLeavePageService } from "../../../../services/page/WarningLeavePageService";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { breadcrumbsStorage } from "../../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../../services/breadcrumbs/EntityTypeFromPathModel";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();

  const currentJobId = urlParamsService.getJobId()!;

  const [currentJobStorage] = useState(() =>
    CurrentJobStorageModel.create({ currentJobId: currentJobId }, { api })
  );

  const jobMenuItems = useMemo(() => {
    return JobMenuItemsModel.create({
      jobId: currentJobId,
      isLoading: currentJobStorage.isLoading,
      dynamicMenuItemPage: JobDynamicMenuItemsPages.EditJob,
      numberOfSewerObjects: currentJobStorage.currentJob.sewerObjectsCount,
    });
  }, [currentJobId, currentJobStorage.currentJob.sewerObjectsCount, currentJobStorage.isLoading]);

  const menuItems = { staticItems: jobMenuItems.getStaticItems() };

  const initialValues = useMemo(() => {
    return {
      id: currentJobStorage.currentJob.id,
      name: currentJobStorage.currentJob.name,
      status: currentJobStorage.currentJob.status,
      assignedUser: currentJobStorage.currentJob.assignedUser?.id
        ? {
            id: currentJobStorage.currentJob.assignedUser.id,
            name: currentJobStorage.currentJob.assignedUser.name,
          }
        : null,
      availableFrom: new Date(currentJobStorage.currentJob.availableFrom),
      deadline: currentJobStorage.currentJob.deadline
        ? new Date(currentJobStorage.currentJob.deadline)
        : null,
      description: currentJobStorage.currentJob.description,
      type: currentJobStorage.currentJob.type,
      priority: currentJobStorage.currentJob.priority,
      project: {
        id: currentJobStorage.currentJob.project.id,
        name: currentJobStorage.currentJob.project.name,
      },
    };
  }, [
    currentJobStorage.currentJob.assignedUser,
    currentJobStorage.currentJob.availableFrom,
    currentJobStorage.currentJob.deadline,
    currentJobStorage.currentJob.description,
    currentJobStorage.currentJob.id,
    currentJobStorage.currentJob.name,
    currentJobStorage.currentJob.priority,
    currentJobStorage.currentJob.project,
    currentJobStorage.currentJob.status,
    currentJobStorage.currentJob.type,
  ]);

  const goToJobPage = useCallback(
    () => history.push(`/jobs/${currentJobStorage.currentJob.id}`),
    [currentJobStorage.currentJob.id, history]
  );

  const handleSubmit = async (values: FormikValues, formActions: FormikHelpers<FormikValues>) => {
    const availableFrom = dateService.dateToUTC(values.availableFrom);

    let deadline: any = null;
    if (values.deadline !== null) {
      deadline = dateService.dateToUTC(values.deadline);
    }
    const jobToEdit: JobInput = {
      id: currentJobStorage.currentJob.id,
      type: values.type,
      priority: values.priority,
      assignedUserId: values.assignedUser ? values.assignedUser.id : null,
      name: values.name,
      status: Number(values.status),
      timeline: {
        availableFrom: availableFrom,
        deadline: deadline,
      },
      description: values.description,
      projectId: values.project.id,
    };
    try {
      await currentJobStorage.editJobDetails(jobToEdit);
      warningLeavePageService.setIsDirty(false);
      return goToJobPage();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(values, formActions);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  if (
    !currentJobStorage.isLoading &&
    permissionsService.userHasPermissionTo(EditAssignedJob) &&
    !currentJobStorage.currentUserIsAssignedToJob
  )
    return <Redirect to="/404" />;

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([{
      entity: EntityTypeFromPath.Job,
      name: `${currentJobStorage.currentJob.name}`
    }]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentJobStorage.currentJob.name, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[EditJobPermission, EditAssignedJob]}
      titleKey="EditJob.title"
      titleContext={{ jobName: currentJobStorage.currentJob.name }}
      menuItems={menuItems}
      id="edit-job"
      isLoading={currentJobStorage.isLoading}
    >
      <Form
        formikOptions={{
          initialValues: initialValues,
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          enableReinitialize: true,
        }}
        onCancel={goToJobPage}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.jobs.details">
                <SubSection>
                  <EditJobName name={values.name} />
                  <EditProject
                    project={values.project}
                    setFieldValue={setFieldValue}
                    disabled={!permissionsService.userHasPermissionTo(AssignAnyProjectAndUserToJob)}
                  />
                  <EditJobStatus status={values.status} setFieldValue={setFieldValue} />
                </SubSection>
                <SubSection>
                  <EditJobType type={values.type} setFieldValue={setFieldValue} />
                  <EditAssignedUser
                    assignedUser={values.assignedUser}
                    setFieldValue={setFieldValue}
                    disabled={!permissionsService.userHasPermissionTo(AssignAnyProjectAndUserToJob)}
                  />
                  <EditPriority priority={values.priority} setFieldValue={setFieldValue} />
                </SubSection>
                <SubSection>
                  <EditAvailableFromAndDeadlineDates
                    availableFrom={values.availableFrom}
                    deadline={values.deadline}
                    setFieldValue={setFieldValue}
                  />
                </SubSection>
                <SubSection>
                  <DisplayDateCreated date={currentJobStorage.currentJob.created} />
                  <DisplayCreatedBy createdBy={currentJobStorage.currentJob.createdBy.name} />
                </SubSection>
              </DataSection>
              <EditDescriptionSection description={values.description} />
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
